import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { CellComponentProps } from '../types';
import { Person } from '../../../types/prediction-engine';
import { getPridectionNameSuggestions } from '../../../api/prediction-engine';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PersonMultiSelectProps extends CellComponentProps {
  roleType: 'actor' | 'director' | 'writer' | 'producer' | 'star';
  disabled?: boolean;
  error?: string | boolean;
}

const PersonAvatar: React.FC<{
  src?: string;
  name: string;
  className?: string;
}> = ({ src, name, className }) => {
  const [imageError, setImageError] = useState(false);

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Avatar className={cn('tw-h-6 tw-w-6 tw-mr-2 tw-flex-shrink-0', className)}>
      {src && !imageError ? (
        <AvatarImage
          src={src}
          alt={`${name} avatar`}
          onError={() => setImageError(true)}
          className="tw-h-full tw-w-full tw-object-cover"
        />
      ) : (
        <AvatarFallback className="tw-bg-slate-500 tw-text-white tw-text-xs tw-font-semibold tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center">
          {initials}
        </AvatarFallback>
      )}
    </Avatar>
  );
};

const createTooltipContent = (
  person: Person,
  onNavigate: (personId: string) => void,
) => (
  <div className="tw-flex tw-items-center tw-gap-3">
    <PersonAvatar src={person.profile_picture_url} name={person.name} />
    <div className="tw-flex tw-flex-col">
      <span className="tw-font-semibold tw-text-sm">{person.name}</span>
      {person.id && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(person.id!);
          }}
          className="tw-text-xs tw-text-gray-300 hover:tw-text-white tw-underline tw-mt-1 tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer tw-text-left"
        >
          View Profile →
        </button>
      )}
    </div>
  </div>
);

const PersonMultiSelectWithTooltips = React.forwardRef<
  any,
  PersonMultiSelectProps
>(
  (
    {
      value,
      onChange,
      roleType,
      isExpanded = false,
      disabled = false,
      error,
      column,
      rowIndex,
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<Person[]>([]);

    const currentPersons = value as Person[];

    const isInvalid =
      typeof column?.cell?.invalid === 'function'
        ? column.cell.invalid(rowIndex)
        : column?.cell?.invalid || Boolean(error);

    const errorMessage = typeof error === 'string' ? error : '';

    // Right under the button rather than after the chips, which an expanded
    // row can scroll out of view.
    const errorText = errorMessage ? (
      // Table cells are nowrap; the message has to wrap within the column.
      <p className="tw-mb-0 tw-whitespace-normal tw-text-xs tw-text-red-600">
        {errorMessage}
      </p>
    ) : null;

    const displayError =
      errorMessage ||
      (isInvalid ? `Please select at least one ${roleType}` : '');

    const handleNavigateToPerson = useCallback((personId: string) => {
      window.open(`/person?id=${personId}`, '_blank', 'noopener,noreferrer');
    }, []);

    // Expose focus method via ref
    React.useImperativeHandle(ref, () => ({
      focus: () => {
        setOpen(true);
      },
    }));

    const searchPersons = useCallback(
      async (query: string): Promise<Person[]> => {
        setIsLoading(true);
        try {
          const persons = await getPridectionNameSuggestions(query, roleType);
          return persons;
        } catch (error) {
          console.error('Error loading suggestions:', error);
          return [];
        } finally {
          setIsLoading(false);
        }
      },
      [roleType],
    );

    const getPersonKey = useCallback((person: Person) => person.id, []);
    const getPersonLabel = useCallback((person: Person) => person.name, []);

    const renderPersonItem = useCallback(
      (person: Person, isSelected: boolean, onClick: () => void) => {
        return (
          <Tooltip>
            <TooltipTrigger className="tw-w-full">
              <CommandItem
                key={person.id}
                value={person.id}
                onSelect={onClick}
                className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-cursor-pointer hover:tw-bg-gray-100"
              >
                <div className="tw-flex tw-items-center tw-flex-1">
                  <PersonAvatar
                    src={person.profile_picture_url}
                    name={person.name}
                  />
                  <span className="tw-text-sm">{person.name}</span>
                </div>
                <div
                  className={cn(
                    'tw-h-4 tw-w-4 tw-border tw-rounded-sm tw-flex tw-items-center tw-justify-center',
                    isSelected
                      ? 'tw-bg-primary tw-border-primary'
                      : 'tw-border-input',
                  )}
                >
                  {isSelected && (
                    <Check className="tw-h-3 tw-w-3 tw-text-primary-foreground" />
                  )}
                </div>
              </CommandItem>
            </TooltipTrigger>
            <TooltipContent side="left" className="tw-z-[9999]" sideOffset={8}>
              {createTooltipContent(person, handleNavigateToPerson)}
            </TooltipContent>
          </Tooltip>
        );
      },
      [handleNavigateToPerson],
    );

    const renderPersonChip = useCallback(
      (person: Person, onRemove: (key: string) => void) => {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="tw-inline-flex tw-items-center tw-gap-1 tw-max-w-[150px] tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80 tw-transition-colors tw-cursor-default">
                <PersonAvatar
                  src={person.profile_picture_url}
                  name={person.name}
                  className="tw-h-4 tw-w-4 tw-mr-1"
                />
                <span className="tw-truncate">{person.name}</span>
                {!disabled && person.id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(person.id!);
                    }}
                    type="button"
                    className="tw-ml-1 tw-h-3 tw-w-3 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-secondary-foreground/70 hover:tw-text-secondary-foreground hover:tw-bg-secondary-foreground/20 tw-transition-colors"
                  >
                    <X className="tw-h-2 tw-w-2" />
                  </button>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="left">
              {createTooltipContent(person, handleNavigateToPerson)}
            </TooltipContent>
          </Tooltip>
        );
      },
      [disabled, handleNavigateToPerson],
    );

    const updatePersons = useCallback(
      (persons: Person[]) => {
        onChange(persons);
      },
      [onChange],
    );

    const getDisplayText = useCallback(() => {
      const isEmpty = currentPersons.length === 0;
      return isEmpty
        ? `Select ${roleType}s...`
        : `${currentPersons.length} ${roleType}${currentPersons.length !== 1 ? 's' : ''} selected`;
    }, [currentPersons.length, roleType]);

    // Handle search with debouncing
    useEffect(() => {
      if (!searchValue.trim()) {
        setSearchResults([]);
        return;
      }

      const timeoutId = setTimeout(async () => {
        try {
          const results = await searchPersons(searchValue);
          setSearchResults(results);
        } catch (error) {
          console.error('Error loading search results:', error);
          setSearchResults([]);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }, [searchValue, searchPersons]);

    const displayText =
      currentPersons.length === 0
        ? `Select ${roleType}s...`
        : `${currentPersons.length} ${roleType}${currentPersons.length !== 1 ? 's' : ''} selected`;

    const availablePersons = searchValue.trim() ? searchResults : [];

    // Reset search when popover closes
    const handleOpenChange = useCallback((newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen) {
        setSearchValue('');
        setSearchResults([]);
      }
    }, []);

    if (!isExpanded) {
      return (
        <div className="tw-relative">
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  'tw-w-full tw-justify-between tw-h-auto tw-min-h-[2.5rem] tw-text-sm tw-transition-colors',
                  isInvalid &&
                    '!tw-border-red-500 focus-visible:!tw-ring-red-500',
                )}
                disabled={disabled}
              >
                <span
                  className={cn(
                    currentPersons.length === 0
                      ? 'tw-text-muted-foreground'
                      : 'tw-text-foreground',
                  )}
                >
                  {displayText}
                </span>
                <ChevronsUpDown className="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="tw-w-80 tw-p-0 tw-z-50" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={`Search ${roleType}s...`}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  className="tw-border-none focus:tw-ring-0 focus:tw-border-none focus:tw-outline-none"
                />
                <CommandList className="tw-max-h-60">
                  {!searchValue.trim() &&
                    currentPersons.length === 0 &&
                    !isLoading && (
                      <CommandEmpty>
                        Type to search for {roleType}s...
                      </CommandEmpty>
                    )}
                  {isLoading && <CommandEmpty>Loading...</CommandEmpty>}
                  {searchValue.trim() &&
                    !isLoading &&
                    availablePersons.length === 0 &&
                    currentPersons.length === 0 && (
                      <CommandEmpty>No results found</CommandEmpty>
                    )}
                  {availablePersons.length > 0 && (
                    <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                      Available
                    </div>
                  )}
                  {availablePersons.map((person) => {
                    const isSelected = currentPersons.some(
                      (selected) => selected.id === person.id,
                    );
                    return renderPersonItem(person, isSelected, () => {
                      if (isSelected) {
                        const newPersons = currentPersons.filter(
                          (p) => p.id !== person.id,
                        );
                        updatePersons(newPersons);
                      } else {
                        updatePersons([...currentPersons, person]);
                      }
                    });
                  })}
                  {currentPersons.length > 0 && (
                    <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                      Selected ({currentPersons.length})
                    </div>
                  )}
                  {currentPersons.map((person) =>
                    renderPersonItem(person, true, () => {
                      const newPersons = currentPersons.filter(
                        (p) => p.id !== person.id,
                      );
                      updatePersons(newPersons);
                    }),
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {errorText && <div className="tw-mt-1">{errorText}</div>}
          {/* {isInvalid && displayError && (
          <span className="tw-absolute tw-top-full tw-left-0 tw-text-xs tw-text-red-500 tw-mt-1">
            {displayError}
          </span>
        )} */}
        </div>
      );
    }

    return (
      <div className="tw-relative">
        <div className="tw-space-y-2">
          {!disabled && (
            <Popover open={open} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={cn(
                    'tw-w-full tw-justify-between tw-h-auto tw-min-h-[2.5rem] tw-text-sm tw-transition-colors',
                    isInvalid &&
                      '!tw-border-red-500 focus-visible:!tw-ring-red-500',
                  )}
                  disabled={disabled}
                >
                  <span
                    className={cn(
                      currentPersons.length === 0
                        ? 'tw-text-muted-foreground'
                        : 'tw-text-foreground',
                      'tw-text-sm',
                    )}
                  >
                    {currentPersons.length === 0
                      ? `Select ${roleType}s...`
                      : `Add more ${roleType}s...`}
                  </span>
                  <ChevronsUpDown className="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="tw-w-80 tw-p-0 tw-z-50 !tw-p-0"
                align="start"
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={`Search ${roleType}s...`}
                    value={searchValue}
                    onValueChange={setSearchValue}
                    className="tw-border-none focus:tw-ring-0 focus:tw-border-none focus:tw-outline-none"
                  />
                  <CommandList className="tw-max-h-60">
                    {!searchValue.trim() &&
                      currentPersons.length === 0 &&
                      !isLoading && (
                        <CommandEmpty>
                          Type to search for {roleType}s...
                        </CommandEmpty>
                      )}
                    {isLoading && <CommandEmpty>Loading...</CommandEmpty>}
                    {searchValue.trim() &&
                      !isLoading &&
                      availablePersons.length === 0 &&
                      currentPersons.length === 0 && (
                        <CommandEmpty>No results found</CommandEmpty>
                      )}
                    {availablePersons.length > 0 && (
                      <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                        Available
                      </div>
                    )}
                    {availablePersons.map((person) => {
                      const isSelected = currentPersons.some(
                        (selected) => selected.id === person.id,
                      );
                      return renderPersonItem(person, isSelected, () => {
                        if (isSelected) {
                          const newPersons = currentPersons.filter(
                            (p) => p.id !== person.id,
                          );
                          updatePersons(newPersons);
                        } else {
                          updatePersons([...currentPersons, person]);
                        }
                      });
                    })}
                    {currentPersons.length > 0 && (
                      <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                        Selected ({currentPersons.length})
                      </div>
                    )}
                    {currentPersons.map((person) =>
                      renderPersonItem(person, true, () => {
                        const newPersons = currentPersons.filter(
                          (p) => p.id !== person.id,
                        );
                        updatePersons(newPersons);
                      }),
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          {errorText}

          {currentPersons.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-1">
              {currentPersons.map((person) =>
                renderPersonChip(person, (id) => {
                  const newPersons = currentPersons.filter((p) => p.id !== id);
                  updatePersons(newPersons);
                }),
              )}
            </div>
          )}
        </div>
        {/* {isInvalid && displayError && (
        <span className="tw-absolute tw-top-full tw-left-0 tw-text-xs tw-text-red-500 tw-mt-1">
          {displayError}
        </span>
      )} */}
      </div>
    );
  },
);

export default PersonMultiSelectWithTooltips;
